import {
  accessProvisionApi,
  incidentApi,
  listStatusParams,
  softwareApi,
} from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { unwrapData, unwrapList } from '../../utils/responseHelpers';

const PROCEDURE = {
  SYSTEM: 'SYSTEM',
  INCIDENT: 'INCIDENT',
  CHANGE: 'CHANGE',
  MONITORING_REVIEW: 'MONITORING_REVIEW',
  REVIEW: 'REVIEW',
  ACCESS_PROVISION: 'ACCESS_PROVISION',
};

function firstText(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value == null) continue;
    const text = String(value).trim();
    if (text && text !== '—') return text;
  }
  return '';
}

function assetNamesFromIncident(incident) {
  if (incident?.assetSummary) return String(incident.assetSummary);
  return (incident?.assets || [])
    .map((a) => a.assetName || a.assetId || a.assetTag)
    .filter(Boolean)
    .join(', ');
}

function mapSrApproval(approval, extras = {}) {
  const incidentId = approval.incidentId ?? extras.incidentId ?? null;
  const changeManagementId = approval.changeManagementId ?? extras.changeManagementId ?? null;
  const monitoringReviewId =
    approval.monitoringReviewId ?? extras.monitoringReviewId ?? null;
  const softwareMonitoringAndReviewId =
    approval.softwareMonitoringAndReviewId ?? extras.softwareMonitoringAndReviewId ?? null;
  const reviewId = approval.reviewId ?? extras.reviewId ?? null;
  const softwareId = approval.softwareId ?? extras.softwareId ?? null;
  const softwareName = firstText(approval.softwareName, extras.softwareName);
  const incidentTicket = firstText(approval.incidentTicket, extras.incidentTicket);
  const assetSummary = firstText(approval.assetSummary, extras.assetSummary);
  const changeTitle = firstText(approval.changeTitle, extras.changeTitle);
  const monitoringReviewArea = firstText(
    approval.monitoringReviewArea,
    extras.monitoringReviewArea
  );

  let procedureType = approval.procedureType || extras.procedureType || '';
  if (!procedureType) {
    if (incidentId) procedureType = PROCEDURE.INCIDENT;
    else if (changeManagementId) procedureType = PROCEDURE.CHANGE;
    else if (softwareMonitoringAndReviewId || monitoringReviewId) {
      procedureType = PROCEDURE.MONITORING_REVIEW;
    } else if (reviewId) procedureType = PROCEDURE.REVIEW;
    else procedureType = PROCEDURE.SYSTEM;
  }

  const procedureLabel =
    approval.procedureLabel
    || extras.procedureLabel
    || (procedureType === PROCEDURE.INCIDENT
      ? (softwareId ? 'System incident' : 'IT asset incident')
      : procedureType === PROCEDURE.CHANGE
        ? 'Change management'
        : procedureType === PROCEDURE.MONITORING_REVIEW
          ? 'Monitoring review'
          : procedureType === PROCEDURE.REVIEW
            ? 'System review'
            : 'Registered system');

  const subjectLabel = firstText(
    approval.subjectLabel,
    extras.subjectLabel,
    incidentTicket,
    assetSummary,
    changeTitle,
    monitoringReviewArea,
    softwareName,
    approval.approverName ? `Approval #${approval.id}` : ''
  );

  return {
    ...approval,
    id: `sr-${approval.id}`,
    approvalRecordId: approval.id,
    procedureType,
    procedureLabel,
    subjectLabel: subjectLabel || '—',
    softwareId,
    softwareName: softwareName || '—',
    incidentId,
    incidentTicket: incidentTicket || '—',
    assetSummary: assetSummary || '—',
    changeManagementId,
    changeTitle: changeTitle || '—',
    monitoringReviewId,
    softwareMonitoringAndReviewId,
    reviewId,
    monitoringReviewArea: monitoringReviewArea || '—',
    accessProvisionId: null,
    recordStatus: extras.recordStatus || 'ACTIVE',
    softwareStatus: extras.softwareStatus || extras.recordStatus || 'ACTIVE',
  };
}

function mapAccessApproval(provision, approval) {
  const requestType = formatEnumLabel(provision.requestType) || provision.requestType || '';
  const requestedBy = firstText(provision.requestedBy, provision.requestedByEmail);
  const reason = firstText(provision.reasonForRequest);
  const subjectLabel = firstText(
    [requestType, requestedBy].filter(Boolean).join(' · '),
    reason,
    provision.id != null ? `Access request #${provision.id}` : ''
  );
  return {
    ...approval,
    id: `ap-${approval.id}`,
    approvalRecordId: approval.id,
    procedureType: PROCEDURE.ACCESS_PROVISION,
    procedureLabel: 'Access Administration',
    subjectLabel: subjectLabel || '—',
    softwareId: null,
    softwareName: '—',
    incidentId: null,
    incidentTicket: '—',
    assetSummary: '—',
    changeManagementId: null,
    changeTitle: '—',
    monitoringReviewId: null,
    softwareMonitoringAndReviewId: null,
    reviewId: null,
    monitoringReviewArea: '—',
    accessProvisionId: provision.id,
    requestType: provision.requestType || '',
    requestedBy,
    reasonForRequest: reason,
    jobPosition: approval.jobPosition || '',
    approverName: approval.approverName || '',
    approverRole: approval.approverRole || '',
    approverCommittee: approval.approverCommittee || '',
    committee: approval.approverCommittee || '',
    reviewedBy: approval.approverName || '',
    approvalDate: approval.approvalDate || '',
    approvalStatus: approval.approvalStatus || '',
    approvalLevel: approval.approvalLevel || '',
    approvalComments: approval.approvalComments || '',
    signatureConfirmation: approval.signatureConfirmation || '',
    recordStatus: provision.status || 'ACTIVE',
    softwareStatus: provision.status || 'ACTIVE',
  };
}

export function approvalViewPath(row) {
  if (!row) return '/system-register/approvals';
  if (row.procedureType === PROCEDURE.ACCESS_PROVISION && row.accessProvisionId) {
    return `/system-register/access-provisions/view?id=${row.accessProvisionId}`;
  }
  if (row.procedureType === PROCEDURE.INCIDENT && row.incidentId) {
    const qs = new URLSearchParams({ id: String(row.incidentId) });
    if (row.softwareId) qs.set('softwareId', String(row.softwareId));
    return `/system-register/incidents/view?${qs.toString()}`;
  }
  if (row.procedureType === PROCEDURE.CHANGE && row.changeManagementId) {
    const qs = new URLSearchParams({ id: String(row.changeManagementId) });
    if (row.softwareId) qs.set('softwareId', String(row.softwareId));
    return `/system-register/change-managements/view?${qs.toString()}`;
  }
  if (
    (row.procedureType === PROCEDURE.MONITORING_REVIEW || row.softwareMonitoringAndReviewId)
    && (row.softwareMonitoringAndReviewId || row.monitoringReviewId)
    && row.softwareId
  ) {
    const id = row.softwareMonitoringAndReviewId || row.monitoringReviewId;
    return `/system-register/reviews/view?softwareId=${row.softwareId}&id=${id}`;
  }
  if (row.softwareId && row.approvalRecordId) {
    return `/system-register/approvals/view?softwareId=${row.softwareId}&id=${row.approvalRecordId}`;
  }
  return '/system-register/approvals';
}

export async function fetchApprovalDirectory(params = {}) {
  const status = params.status || listStatusParams(false).status;
  const listParams = { status };

  const settled = await Promise.allSettled([
    softwareApi.listNested({ collection: 'approvals', ...listParams }),
    incidentApi.list(listParams),
    softwareApi.listNested({ collection: 'changeManagements', ...listParams }),
    softwareApi.listNested({ collection: 'monitoringReviews', ...listParams }),
  ]);
  const [systemsRes, incidentsRes, changesRes, reviewsRes] = settled.map((result) =>
    result.status === 'fulfilled' ? result.value : { data: { data: [] } }
  );

  const rows = [];
  const seenSr = new Set();

  const addSr = (approval, extras) => {
    if (!approval?.id) return;
    const key = `sr-${approval.id}`;
    if (seenSr.has(key)) return;
    seenSr.add(key);
    rows.push(mapSrApproval(approval, extras));
  };

  unwrapList(systemsRes).forEach((system) => {
    (system.approvals ?? []).forEach((approval) =>
      addSr(approval, {
        softwareId: system.id,
        softwareName: system.softwareName,
        softwareStatus: system.status,
        procedureType: PROCEDURE.SYSTEM,
        procedureLabel: 'Registered system',
        subjectLabel: system.softwareName,
      })
    );
  });

  unwrapList(changesRes).forEach((system) => {
    (system.changeManagements ?? []).forEach((change) => {
      (change.approvals ?? []).forEach((approval) =>
        addSr(approval, {
          softwareId: system.id,
          softwareName: system.softwareName,
          softwareStatus: system.status,
          changeManagementId: change.id,
          changeTitle: change.title,
          procedureType: PROCEDURE.CHANGE,
          procedureLabel: 'Change management',
          subjectLabel: firstText(change.title, system.softwareName),
        })
      );
    });
  });

  unwrapList(reviewsRes).forEach((system) => {
    (system.monitoringReviews ?? []).forEach((review) => {
      (review.approvals ?? []).forEach((approval) =>
        addSr(approval, {
          softwareId: system.id,
          softwareName: system.softwareName,
          softwareStatus: system.status,
          softwareMonitoringAndReviewId: review.id,
          monitoringReviewId: review.monitoringReviewId,
          monitoringReviewArea: review.reviewAreaControl,
          procedureType: PROCEDURE.MONITORING_REVIEW,
          procedureLabel: 'Monitoring review',
          subjectLabel: firstText(review.reviewAreaControl, system.softwareName),
        })
      );
    });
  });

  unwrapList(incidentsRes).forEach((incident) => {
    const assets = assetNamesFromIncident(incident);
    const assetOnly = !incident.softwareId && Boolean(assets);
    (incident.approvals ?? []).forEach((approval) =>
      addSr(approval, {
        softwareId: incident.softwareId,
        softwareName: incident.softwareName,
        softwareStatus: incident.recordStatus,
        recordStatus: incident.recordStatus,
        incidentId: incident.id,
        incidentTicket: incident.incidentTicket,
        assetSummary: assets,
        procedureType: PROCEDURE.INCIDENT,
        procedureLabel: assetOnly ? 'IT asset incident' : 'System incident',
        subjectLabel: firstText(
          incident.incidentTicket,
          incident.softwareName,
          assets,
          incident.incidentDetails
        ),
      })
    );
  });

  const accessRows = await loadAccessAdministrationApprovals(listParams);
  rows.push(...accessRows);

  return { data: { data: rows } };
}

async function loadAccessAdministrationApprovals(listParams) {
  let provisions = [];
  try {
    provisions = unwrapList(await accessProvisionApi.listApprovals(listParams));
  } catch {
    provisions = [];
  }
  if (!provisions.length) {
    try {
      provisions = unwrapList(await accessProvisionApi.list(listParams));
    } catch {
      provisions = [];
    }
  }
  if (!provisions.length && listParams?.status !== 'ALL') {
    try {
      provisions = unwrapList(await accessProvisionApi.listApprovals({ status: 'ALL' }));
    } catch {
      try {
        provisions = unwrapList(await accessProvisionApi.list({ status: 'ALL' }));
      } catch {
        provisions = [];
      }
    }
  }

  const hydrated = await Promise.all(
    provisions.map(async (provision) => {
      if (Array.isArray(provision?.approvals) && provision.approvals.length > 0) {
        return provision;
      }
      if (provision?.id == null) return provision;
      try {
        return unwrapData(await accessProvisionApi.get(provision.id)) || provision;
      } catch {
        return provision;
      }
    })
  );

  const rows = [];
  hydrated.forEach((provision) => {
    (provision?.approvals ?? []).forEach((approval) => {
      if (approval == null) return;
      const withId = {
        ...approval,
        id: approval.id ?? `slot-${provision.id}-${approval.slot ?? 'x'}`,
      };
      rows.push(mapAccessApproval(provision, withId));
    });
  });
  return rows;
}

export { PROCEDURE };
